import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
} from '@mui/material';
import {
  Email as EmailIcon,
  Phone as PhoneIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { useRecipe } from '../context/RecipeContext';
import landingBackground from '../images/landingbackground3.jpg';

const Contact: React.FC = () => {
  const navigate = useNavigate();
  const { setMode } = useRecipe();

  const handleGetStarted = () => {
    setMode('generate');
    navigate('/composer');
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', maxWidth: '1200px', mx: 'auto' }}>
      {/* Hero Section */}
      <Box
        sx={{
          height: { xs: '40vh', md: '50vh' },
          backgroundImage: `url(${landingBackground})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: 'blur(1px)',
          position: 'relative',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            px: 2,
            textAlign: 'center',
          }}
        >
          <Typography
            variant="h3"
            component="h1"
            sx={{
              color: 'white',
              fontWeight: 'bold',
              textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
              mb: 2,
            }}
          >
            Contact Us
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: 'white',
              fontWeight: 'bold',
              textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
              mb: 1,
            }}
          >
            We'd Love to Hear From You
          </Typography>
        </Box>
      </Box>

      {/* Content Section */}
      <Box sx={{ p: { xs: 2, md: 4 }, flexGrow: 1 }}>
        <Typography
          variant="h4"
          component="h2"
          sx={{
            textAlign: 'center',
            mb: 4,
            fontWeight: 'bold',
            color: 'primary.main'
          }}
        >
          Get in Touch
        </Typography>

        <Typography
          variant="body1"
          sx={{
            mb: 4,
            textAlign: 'center',
            maxWidth: '800px',
            mx: 'auto',
            lineHeight: 1.6,
            fontSize: { xs: '1rem', md: '1.1rem' }
          }}
        >
          Have questions, feedback, or suggestions? We're here to help! Reach out to us through any of the channels below,
          and we'll get back to you as soon as possible.
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
            gap: 4,
            mb: 4
          }}
        >
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" component="h3" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <EmailIcon sx={{ mr: 1, color: 'primary.main' }} />
                Email Us
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Send us an email and we'll respond within 24 hours.
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                info@aisuretech.com
              </Typography>
            </CardContent>
          </Card>

          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" component="h3" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <PhoneIcon sx={{ mr: 1, color: 'primary.main' }} />
                Support Hours
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Our support team is available during these hours.
                </Typography>
                <Typography variant="body1">
                  Monday - Friday: 9:00 AM - 6:00 PM EST<br />
                  Saturday: 10:00 AM - 4:00 PM EST<br />
                  Sunday: Closed
                </Typography>
              </CardContent>
            </Card>
        </Box>

        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography
            variant="h5"
            component="h3"
            sx={{
              mb: 3,
              fontWeight: 'bold',
              color: 'primary.main'
            }}
          >
            Ready to Start Cooking?
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<SendIcon />}
            onClick={handleGetStarted}
            sx={{
              minWidth: 200,
              fontSize: '1.1rem',
              py: 1.5,
              px: 4,
            }}
          >
            Get Started
          </Button>
        </Box>

        <Box sx={{ textAlign: 'center', py: 3, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="body2" color="text.secondary">
            © 2025 Create A Dish. Made with ❤️ for food lovers everywhere.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default Contact;